import React from "react";
import ContentLoader from "react-content-loader";

const Loader = ({ variant = "spinner" }) => {
  
  // Spinner variant (default - backward compatible)
  if (variant === "spinner") {
    const loaderStyle = {
      border: "4px solid transparent",
      borderTopColor: "var(--photon-chat-accent)",
      borderRadius: "50%",
      width: "3rem",
      height: "3rem",
      animation: "spin 1s linear infinite",
    };

    return (
      <div style={loaderStyle}>
        <style>
          {`
            @keyframes spin {
              0% {
                transform: rotate(0deg);
              }
              100% {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    );
  }

  // Chat skeleton variant
  if (variant === "chat") {
    return (
      <div className="flex flex-col h-full w-full">
        {/* Header Skeleton */}
        <div className="flex-shrink-0 p-4 border-b dark:border-edge-gray-700">
          <ContentLoader
            speed={2}
            width="100%"
            height={60}
            backgroundColor="var(--photon-chat-surface-muted)"
            foregroundColor="var(--photon-chat-accent-soft)"
          >
            <circle cx="30" cy="30" r="20" />
            <rect x="60" y="15" rx="4" ry="4" width="150" height="12" />
            <rect x="60" y="35" rx="3" ry="3" width="100" height="10" />
          </ContentLoader>
        </div>

        {/* Messages Skeleton */}
        <div className="flex-1 overflow-hidden p-4 space-y-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* User Message */}
            <div className="flex justify-end">
              <ContentLoader
                speed={2}
                width={400}
                height={80}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <rect x="50" y="0" rx="12" ry="12" width="350" height="80" />
              </ContentLoader>
            </div>

            {/* AI Message */}
            <div className="flex justify-start">
              <ContentLoader
                speed={2}
                width={500}
                height={120}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <circle cx="20" cy="20" r="16" />
                <rect x="50" y="0" rx="12" ry="12" width="450" height="120" />
              </ContentLoader>
            </div>

            {/* User Message */}
            <div className="flex justify-end">
              <ContentLoader
                speed={2}
                width={350}
                height={60}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <rect x="50" y="0" rx="12" ry="12" width="300" height="60" />
              </ContentLoader>
            </div>

            {/* AI Message */}
            <div className="flex justify-start">
              <ContentLoader
                speed={2}
                width={480}
                height={100}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <circle cx="20" cy="20" r="16" />
                <rect x="50" y="0" rx="12" ry="12" width="430" height="100" />
              </ContentLoader>
            </div>
          </div>
        </div>

        {/* Input Skeleton */}
        <div className="flex-shrink-0 p-4 border-t dark:border-edge-gray-700">
          <div className="max-w-4xl mx-auto">
            <ContentLoader
              speed={2}
              width="100%"
              height={60}
              backgroundColor="var(--photon-chat-surface-muted)"
              foregroundColor="var(--photon-chat-accent-soft)"
            >
              <rect x="0" y="10" rx="20" ry="20" width="100%" height="50" />
            </ContentLoader>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard skeleton variant
  if (variant === "dashboard") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <ContentLoader
            key={i}
            speed={2}
            width="100%"
            height={200}
            backgroundColor="var(--photon-chat-surface-muted)"
            foregroundColor="var(--photon-chat-accent-soft)"
          >
            <rect x="0" y="0" rx="8" ry="8" width="100%" height="200" />
          </ContentLoader>
        ))}
      </div>
    );
  }

  // Form skeleton variant
  if (variant === "form") {
    return (
      <div className="space-y-4 p-4 max-w-2xl mx-auto">
        <ContentLoader
          speed={2}
          width="100%"
          height={400}
          backgroundColor="var(--photon-chat-surface-muted)"
          foregroundColor="var(--photon-chat-accent-soft)"
        >
          <rect x="0" y="0" rx="4" ry="4" width="150" height="20" />
          <rect x="0" y="30" rx="8" ry="8" width="100%" height="50" />
          
          <rect x="0" y="100" rx="4" ry="4" width="150" height="20" />
          <rect x="0" y="130" rx="8" ry="8" width="100%" height="50" />
          
          <rect x="0" y="200" rx="4" ry="4" width="150" height="20" />
          <rect x="0" y="230" rx="8" ry="8" width="100%" height="120" />
          
          <rect x="0" y="370" rx="8" ry="8" width="120" height="40" />
        </ContentLoader>
      </div>
    );
  }

  // Spectra Landing skeleton variant
  if (variant === "spectra-landing") {
    return (
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          {/* Title and Edit Button */}
          <div className="flex justify-between items-center mb-6">
            <ContentLoader
              speed={2}
              width={300}
              height={40}
              backgroundColor="var(--photon-chat-surface-muted)"
              foregroundColor="var(--photon-chat-accent-soft)"
            >
              <rect x="0" y="0" rx="4" ry="4" width="250" height="24" />
              <rect x="0" y="30" rx="3" ry="3" width="180" height="12" />
            </ContentLoader>
            <ContentLoader
              speed={2}
              width={120}
              height={40}
              backgroundColor="var(--photon-chat-surface-muted)"
              foregroundColor="var(--photon-chat-accent-soft)"
            >
              <rect x="0" y="0" rx="8" ry="8" width="120" height="40" />
            </ContentLoader>
          </div>

          {/* Collaborators Section */}
          <div className="bg-fill-white dark:bg-fill-dialog-surface rounded-2xl p-6 border border-edge-gray-200 dark:border-edge-gray-700">
            <ContentLoader
              speed={2}
              width="100%"
              height={120}
              backgroundColor="var(--photon-chat-surface-muted)"
              foregroundColor="var(--photon-chat-accent-soft)"
            >
              <rect x="0" y="0" rx="4" ry="4" width="150" height="20" />
              <circle cx="30" cy="60" r="24" />
              <circle cx="90" cy="60" r="24" />
              <circle cx="150" cy="60" r="24" />
              <circle cx="210" cy="60" r="24" />
              <rect x="0" y="100" rx="3" ry="3" width="200" height="12" />
            </ContentLoader>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Files Section */}
            <div className="bg-fill-white dark:bg-fill-dialog-surface rounded-2xl p-6 border border-edge-gray-200 dark:border-edge-gray-700">
              <ContentLoader
                speed={2}
                width="100%"
                height={240}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <rect x="0" y="0" rx="4" ry="4" width="120" height="20" />
                <rect x="0" y="40" rx="8" ry="8" width="100%" height="50" />
                <rect x="0" y="100" rx="8" ry="8" width="100%" height="50" />
                <rect x="0" y="160" rx="8" ry="8" width="100%" height="50" />
              </ContentLoader>
            </div>

            {/* Activities Section */}
            <div className="bg-fill-white dark:bg-fill-dialog-surface rounded-2xl p-6 border border-edge-gray-200 dark:border-edge-gray-700">
              <ContentLoader
                speed={2}
                width="100%"
                height={240}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <rect x="0" y="0" rx="4" ry="4" width="150" height="20" />
                <circle cx="12" cy="50" r="8" />
                <rect x="30" y="42" rx="3" ry="3" width="80%" height="12" />
                <rect x="30" y="60" rx="3" ry="3" width="60%" height="10" />
                <circle cx="12" cy="100" r="8" />
                <rect x="30" y="92" rx="3" ry="3" width="75%" height="12" />
                <rect x="30" y="110" rx="3" ry="3" width="55%" height="10" />
                <circle cx="12" cy="150" r="8" />
                <rect x="30" y="142" rx="3" ry="3" width="70%" height="12" />
                <rect x="30" y="160" rx="3" ry="3" width="50%" height="10" />
              </ContentLoader>
            </div>
          </div>

          {/* Sparks Section */}
          <div className="bg-fill-white dark:bg-fill-dialog-surface rounded-2xl p-6 border border-edge-gray-200 dark:border-edge-gray-700">
            <ContentLoader
              speed={2}
              width="100%"
              height={200}
              backgroundColor="var(--photon-chat-surface-muted)"
              foregroundColor="var(--photon-chat-accent-soft)"
            >
              <rect x="0" y="0" rx="4" ry="4" width="100" height="20" />
              <rect x="0" y="40" rx="8" ry="8" width="100%" height="60" />
              <rect x="0" y="110" rx="8" ry="8" width="100%" height="60" />
            </ContentLoader>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <ContentLoader
                key={i}
                speed={2}
                width="100%"
                height={80}
                backgroundColor="var(--photon-chat-surface-muted)"
                foregroundColor="var(--photon-chat-accent-soft)"
              >
                <rect x="0" y="0" rx="12" ry="12" width="100%" height="80" />
              </ContentLoader>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default to spinner if unknown variant
  return <Loader variant="spinner" />;
};

export default Loader;
